import { Box, Skeleton, Typography } from "@mui/material";
import {
  FinishingTypes,
  ListingStatus,
  PaymentTerms,
  Track,
} from "../../../types";
import MoreActionsMenu from "@/modules/common/components/ui-kit/menus/more-actions-menu";
import ListingEmptyState from "../common/empty-state";
import { useGetActiveListing } from "../../../hooks/listings.hook";
import { useMemo } from "react";
import { ListingType } from "@/modules/property-tracks/dtos";
import ListingTopBar from "../common/top-bar";
import ListingMedia from "../common/listing-media";
import PublishResaleListing from "./publish-resale-listing";
import AddResaleListing from "./forms/create";
import PropertyInfo from "../../property";
import PropertyPriceData from "../../overview-tab/price-info/property-price-data";
import EditResaleListingMenuItem from "./menu-items/edit-menu-item";
import { ResaleListingFormFields } from "@/modules/property-tracks/utils";
import Status from "@/modules/common/components/ui-kit/status/status";
import InfoCell from "@/modules/common/components/ui-kit/info-cell";
import ViewListing from "./view-listing";
import MarkAsSoldMenuItem from "./menu-items/mark-as-sold-item";
import UnpublishListingMenuItem from "../common/menu-items/unpublish-listing-item";
import BoxWithBackdrop from "@/modules/common/components/ui-kit/box-with-backdrop";
import MarkAsAvailableMenuItem from "./menu-items/mark-as-available-item";

export default function ResaleActiveListingDesktopTab({ track }: { track: Track }) {
  const { data, isLoading } = useGetActiveListing({
    params: {
      propertyUid: track.property.uid,
      listingType: ListingType.RESALE,
    },
  });

  const values = useMemo<ResaleListingFormFields>(
    () => ({
      propertyDetails: {
        compound: track.property.compound
          ? {
              label: track.property.compound.name,
              value: track.property.compound.id,
            }
          : undefined,
        propertyType: track.property.propertyType
          ? {
              label: track.property.propertyType.name,
              value: track.property.propertyType.id,
            }
          : undefined,
        unitId: track.property.unitId ?? undefined,
        numberOfBedrooms: track.property.numberOfBedrooms ?? undefined,
        numberOfBathrooms: track.property.numberOfBathrooms ?? undefined,
        unitArea: track.property.unitArea ?? undefined,
        phase: track.property.phase ?? undefined,
        landArea: track.property.landArea ?? undefined,
        gardenArea: track.property.gardenArea ?? undefined,
        roofArea: track.property.roofArea ?? undefined,
        floorNumber: track.property.floorNumber ?? undefined,
        numberOfFloors: track.property.numberOfFloors ?? undefined,
        deliveryDate: track.property.deliveryDate
          ? new Date(track.property.deliveryDate)
          : undefined,
        lastInstallmentDate: track.property.lastInstallmentDate
          ? new Date(track.property.lastInstallmentDate)
          : undefined,
        readyToMove: track.property.readyToMove ?? undefined,
        description: track.property.description ?? undefined,
        websiteListingId: track.property.websiteListingId ?? undefined,
        finishing: track.property.finishing
          ? {
              label: FinishingTypes[track.property.finishing],
              value: track.property.finishing,
            }
          : undefined,
        amenityIds: new Set(
          track.property.propertyAmenity?.map((item) => item.amenity.id) || []
        ),
      },
      propertyPriceDetails: data?.propertyPrice
        ? {
            price: data.propertyPrice.price,
            askingPrice: data.propertyPrice.askingPrice,
            paymentType: data.propertyPrice.paymentType,
            downPayment: data.propertyPrice.downPayment,
            remainingInstallments: data.propertyPrice.remainingInstallments,
            clubhouse: data.propertyPrice.clubhouse,
            parking: data.propertyPrice.parking,
            maintenance: data.propertyPrice.maintenance,
            durationRemainingMonths: data.propertyPrice.durationRemainingMonths,
            durationRemainingYears: data.propertyPrice.durationRemainingYears,
            installmentFrequency: data.propertyPrice.installmentFrequency
              ? {
                  label: PaymentTerms[data.propertyPrice.installmentFrequency],
                  value: data.propertyPrice.installmentFrequency,
                }
              : undefined,
          }
        : undefined,
      resaleDocumentsDetails: {
        floorPlanUids:
          data?.trackDocuments
            ?.filter((d) => d.documentType === "FloorPlans")
            .map((d) => d.uid) ?? [],
        mediaUids:
          data?.trackDocuments
            ?.filter((d) => d.documentType === "Media")
            .map((d) => d.uid) ?? [],
      },
      listingDescriptionDetails: {
        descriptionAr: data?.descriptionAr ?? undefined,
        descriptionEn: data?.descriptionEn ?? undefined,
      },
    }),
    [track, data]
  );
  
  const renderActions = () => {
    const moreMenuActions: React.ReactNode[] = [];
    const actions: React.ReactNode[] = [];
    if (data?.isSold) {
      moreMenuActions.push(
        <MarkAsAvailableMenuItem listingUid={data.uid} />,
        <UnpublishListingMenuItem listingUid={data.uid} />
      );
    } else {
      switch (data?.status) {
        case ListingStatus.PUBLISHED:
        case ListingStatus.UNPUBLISHED_EDITS:
          actions.push(
            <PublishResaleListing
              listingUid={data.uid}
              trackUid={track.uid}
              values={values}
            />
          );
          moreMenuActions.push([
            <EditResaleListingMenuItem
              listingUid={data.uid}
              trackUid={track.uid}
              values={values}
            />,
            <MarkAsSoldMenuItem listingUid={data.uid} />,
            <UnpublishListingMenuItem listingUid={data.uid} />,
          ]);
          break;
        case ListingStatus.UNPUBLISHED:
          actions.push(
            <PublishResaleListing
              listingUid={data.uid}
              trackUid={track.uid}
              values={values}
            />
          );
          moreMenuActions.push([
            <EditResaleListingMenuItem
              listingUid={data.uid}
              trackUid={track.uid}
              values={values}
            />,
          ]);
          break;
      }
    }

    return (
      <>
        {actions}
        <MoreActionsMenu>{moreMenuActions}</MoreActionsMenu>
      </>
    );
  };

  if (isLoading) return <Skeleton />;

  if (!data)
    return (
      <ListingEmptyState callToAction={<AddResaleListing track={track} />} />
    );
  return (
    <Box
      sx={(t) => ({
        display: "flex",
        flexDirection: "column",
        gap: t.nawy.spacing.md,
      })}
    >
      {/* TOP BAR */}
      <ListingTopBar listing={data} actions={<>{renderActions()}</>} />
      <BoxWithBackdrop
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.md,
        })}
        allowInteractions={true}
        disabled={data.isSold ?? false}
      >
        <Box
          sx={(t) => ({
            display: "flex",
            flexDirection: "column",
            gap: t.nawy.spacing.sm,
            backgroundColor: t.nawy.colors.semantic.layer.primary,
            padding: t.nawy.spacing.md,
            borderRadius: t.nawy.radius.xl,
            postion: "relative",
          })}
        >
          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
          >
            Unit details
          </Typography>
          <PropertyInfo property={track.property} trackTypeName={track.trackType.name} />
          <ViewListing
          compoundId={track.property.compound?.id ?? ""}
          inventoryListingId={data.inventoryListingId}
        />
        </Box>
        <Box
          sx={(t) => ({
            display: "flex",
            gap: t.nawy.spacing.sm,
            // alignItems: "flex-start",
          })}
        >
          <Box
            sx={(t) => ({
              display: "flex",
              flexDirection: "column",
              gap: t.nawy.spacing.sm,
              backgroundColor: t.nawy.colors.semantic.layer.primary,
              padding: t.nawy.spacing.md,
              borderRadius: t.nawy.radius.xl,
              flex: 1,
            })}
          >
            <Box
              sx={(t) => ({
                display: "flex",
                gap: t.nawy.spacing.xs,
                alignItems: "center",
              })}
            >
              <Typography
                sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
              >
                Payment details
              </Typography>
              {!data.propertyPrice?.active && (
                <Status
                  label="Out of sync"
                  variant="secondary"
                  color="grey"
                  width="auto"
                />
              )}
            </Box>

            <PropertyPriceData
              propertyPrice={data.propertyPrice}
              extraFields={true}
            />
          </Box>
          <Box
            sx={(t) => ({
              display: "flex",
              flexDirection: "column",
              gap: t.nawy.spacing.sm,
              backgroundColor: t.nawy.colors.semantic.layer.primary,
              padding: t.nawy.spacing.md,
              borderRadius: t.nawy.radius.xl,
              flex: 1,
            })}
          >
            <Box
              sx={(t) => ({
                display: "flex",
                gap: t.nawy.spacing.xs,
                alignItems: "center",
              })}
            >
              <Typography
                sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
              >
                Unit description
              </Typography>
            </Box>
            <InfoCell title="EN description" description={data.descriptionEn} />
            <InfoCell title="AR description" description={data.descriptionAr} />
          </Box>
        </Box>
        <Box
          sx={(t) => ({
            display: "flex",
            gap: t.nawy.spacing.sm,
            alignItems: "flex-start",
          })}
        >
          <ListingMedia
            title={"Listing Media"}
            documents={data.trackDocuments.filter(
              (d) => d.documentType === "Media"
            )}
          />

          <ListingMedia
            title={"Listing Floorplans"}
            documents={data.trackDocuments.filter(
              (d) => d.documentType === "FloorPlans"
            )}
          />
        </Box>
      </BoxWithBackdrop>
    </Box>
  );
}
